/**
 * Note markup — the tiny formatting dialect a task description is written in.
 *
 * Descriptions are stored as PLAIN TEXT carrying an optional, tiny Markdown subset plus one
 * extension:
 *
 *   **bold**   *italic*   ~~strike~~   `code`   {red}(coloured text)
 *   - bullet list lines
 *
 * It lives in `shared` rather than in the dev app's UI package because three places read it: the
 * board's editor, the board's renderer, and the Worker, which strips it when a task becomes a
 * changelog line destined for a Steam patch-notes box that parses none of this.
 *
 * The text is parsed to a node tree at render time and emitted as React elements only, never as
 * innerHTML, so arbitrary input is inert by construction. Unmatched or malformed delimiters fall
 * back to literal text, which is what keeps a description somebody typed as plain prose
 * rendering exactly as typed.
 */

// Fixed palette, desaturated so a coloured word sits beside the status colours without fighting
// them. A colour a person picked is CONTENT, not UI chrome, so it lives outside the brand's
// "lemon is focus and nothing else" rule.
export const NOTE_COLORS = {
  red: '#e96159',
  orange: '#d98e4a',
  yellow: '#cdb44c',
  green: '#4caf69',
  blue: '#5a9bd4',
  purple: '#9d7bd8',
  pink: '#d876b6',
  gray: '#9897A1',
} as const;

export type NoteColor = keyof typeof NOTE_COLORS;

export type InlineNode =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'break' }
  | { type: 'bold' | 'italic' | 'strike'; children: InlineNode[] }
  | { type: 'color'; color: NoteColor; children: InlineNode[] };

export type BlockNode =
  { type: 'paragraph'; children: InlineNode[] } | { type: 'list'; items: InlineNode[][] };

const COLOR_OPEN = new RegExp(`^\\{(${Object.keys(NOTE_COLORS).join('|')})\\}\\(`);

// Finds the span opened by `delim` at `start`. Markdown-style guards: content
// must be non-empty and must not start or end with whitespace (so "3 * 4 * 5"
// stays literal). Returns the inner content and the index just past the
// closing delimiter, or null to fall back to literal text.
function findSpan(
  src: string,
  start: number,
  delim: string,
): { content: string; end: number } | null {
  const contentStart = start + delim.length;
  if (contentStart >= src.length || src[contentStart] === ' ') return null;
  let close = src.indexOf(delim, contentStart);
  while (close !== -1) {
    const content = src.slice(contentStart, close);
    if (content.length > 0 && !content.endsWith(' ')) return { content, end: close + delim.length };
    close = src.indexOf(delim, close + 1);
  }
  return null;
}

// Index of the ")" balancing the "(" just before `start`, or -1.
function findBalancedParen(src: string, start: number): number {
  let depth = 1;
  for (let i = start; i < src.length; i++) {
    if (src[i] === '(') depth++;
    else if (src[i] === ')' && --depth === 0) return i;
  }
  return -1;
}

// Parses one line (no newlines) of inline markup.
export function parseInline(src: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let literal = '';
  const flush = () => {
    if (literal) {
      nodes.push({ type: 'text', text: literal });
      literal = '';
    }
  };

  let i = 0;
  while (i < src.length) {
    const ch = src[i];

    if (ch === '`') {
      // Code spans are opaque: no nested formatting inside.
      const close = src.indexOf('`', i + 1);
      if (close > i + 1) {
        flush();
        nodes.push({ type: 'code', text: src.slice(i + 1, close) });
        i = close + 1;
        continue;
      }
    } else if (ch === '*' || ch === '~') {
      // "***both***" — bold and italic on the same words, where the two
      // delimiters abut and can't be told apart one pair at a time.
      if (ch === '*' && src.startsWith('***', i)) {
        const span = findSpan(src, i, '***');
        if (span) {
          flush();
          nodes.push({
            type: 'bold',
            children: [{ type: 'italic', children: parseInline(span.content) }],
          });
          i = span.end;
          continue;
        }
      }
      const double = src.startsWith(ch + ch, i);
      // "~" only formats as "~~"; a single tilde is common in paths/versions.
      if (ch === '*' || double) {
        const delim = double ? ch + ch : ch;
        const span = findSpan(src, i, delim);
        if (span) {
          flush();
          const type = ch === '~' ? 'strike' : double ? 'bold' : 'italic';
          nodes.push({ type, children: parseInline(span.content) });
          i = span.end;
          continue;
        }
      }
    } else if (ch === '{') {
      const m = COLOR_OPEN.exec(src.slice(i));
      if (m) {
        const contentStart = i + m[0].length;
        const close = findBalancedParen(src, contentStart);
        if (close > contentStart) {
          flush();
          nodes.push({
            type: 'color',
            color: m[1] as NoteColor,
            children: parseInline(src.slice(contentStart, close)),
          });
          i = close + 1;
          continue;
        }
      }
    }

    literal += ch;
    i++;
  }
  flush();
  return nodes;
}

const BULLET = /^\s*[-*]\s+(.*)$/;

// Parses a full message into blocks: consecutive "- " / "* " lines group into
// one list; other adjacent non-blank lines form a paragraph with hard breaks.
export function parseNoteMarkup(src: string): BlockNode[] {
  const blocks: BlockNode[] = [];
  let para: InlineNode[] | null = null;

  for (const line of src.split(/\r?\n/)) {
    const bullet = BULLET.exec(line);
    if (bullet) {
      para = null;
      // The bullet pattern has one non-optional group, so a match carries it.
      const item = parseInline(bullet[1]!);
      const last = blocks[blocks.length - 1];
      if (last?.type === 'list') last.items.push(item);
      else blocks.push({ type: 'list', items: [item] });
    } else if (!line.trim()) {
      para = null;
    } else if (para) {
      para.push({ type: 'break' }, ...parseInline(line));
    } else {
      para = [...parseInline(line)];
      blocks.push({ type: 'paragraph', children: para });
    }
  }
  return blocks;
}

// First non-blank line, bullet marker dropped — the "subject line" used by
// one-line truncated displays (commit lists).
export function parseNoteSummary(src: string): InlineNode[] {
  const line = src.split(/\r?\n/).find((l) => l.trim());
  if (!line) return [];
  const bullet = BULLET.exec(line);
  return parseInline(bullet ? bullet[1]! : line.trim());
}

// Splits a message into its subject line and the remaining body (or null),
// GitHub-style, for detail views that render the title prominently.
export function splitNoteMarkup(src: string): { title: string; body: string | null } {
  const lines = src.split(/\r?\n/);
  const idx = lines.findIndex((l) => l.trim());
  if (idx === -1) return { title: '', body: null };
  const rest = lines.slice(idx + 1).join('\n');
  // `findIndex` returned a real index, so the row is there.
  return { title: lines[idx]!.trim(), body: rest.trim() ? rest : null };
}

function inlineToText(nodes: InlineNode[]): string {
  return nodes
    .map((n) => {
      switch (n.type) {
        case 'text':
        case 'code':
          return n.text;
        case 'break':
          return '\n';
        default:
          return inlineToText(n.children);
      }
    })
    .join('');
}

// Markup stripped down to plain text — for native dialogs, tooltips, logs.
export function stripNoteMarkup(src: string): string {
  return parseNoteMarkup(src)
    .map((b) =>
      b.type === 'list'
        ? b.items.map((item) => `• ${inlineToText(item)}`).join('\n')
        : inlineToText(b.children),
    )
    .join('\n');
}
